import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircle, XCircle } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { AskMarcusButton } from '@/components/actions/AskMarcusButton';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildManageAction,
  buildScanActionHandlers,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useSummonAurelius } from '@/hooks/useSummonAurelius';
import { queryClient } from '@/queryclient';
import { getSeedTypeConfig } from '@/sections/seeds/config/seedTypeConfigs';
import type { SeedsActionHandlers } from '@/sections/seeds/hooks/actions/useSeedsActionHandlers';
import type { SeedModalHandlers } from '@/sections/seeds/hooks/useSeedsActions';
import {
  isSeedDomain,
  isSeedIP,
  isSeedWebApplication,
  Seed as SeedDiscriminated,
  SeedType,
} from '@/sections/seeds/types';
import { Seed, SeedStatus } from '@/types';

/**
 * Props for SeedsActions component
 *
 * Refactored to use coordinator pattern - receives handlers from parent
 */
interface SeedsActionsProps {
  /** Current seed type (determines which actions are available) */
  seedType: SeedType;
  /** Selected seeds for bulk actions */
  selectedSeeds: Seed[];
  /** Number of selected rows */
  selectedRowsCount: number;
  /** Modal handlers from coordinator */
  modalHandlers: SeedModalHandlers;
  /** Action handlers from coordinator */
  actionHandlers: SeedsActionHandlers;
  /** Callback to clear selection after actions */
  onClearSelection: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Hide the "Add Seed" button (e.g., in drawer context) */
  hideAddSeed?: boolean;
}

/**
 * Seeds section bulk actions toolbar
 *
 * **Refactored Pattern**: Uses coordinator pattern to receive handlers and modal state from parent.
 * This eliminates code duplication and provides a single source of truth for actions.
 *
 * **Configuration-Driven**: Uses SeedTypeConfig to determine which actions to show
 * - Preseed: Only shows approve/reject actions
 * - Domain/IP/Web App: Shows all actions (comment, tag, scan, share, export, status changes)
 *
 * Displays bulk action buttons using shared dropdown components:
 * - AnnotateDropdown (Comment, Tag, Remove Tag) - shown only if config.actions.comment && config.actions.tag
 * - ShareButton (Share URLs) - shown only if config.actions.share
 * - ExportButton (Export) - always enabled (no config check)
 * - ManagedDropdown (Approve, Reject, Freeze, Delete) - config-driven status changes
 * - ScanDropdown (Various scans) - shown only if config.actions.scan
 *
 * Features:
 * - Share URLs: Copies deep links to clipboard for selected seeds
 * - Export: Opens modal for exporting seeds (modal managed by parent)
 * - Status changes: Approve, reject, freeze, delete operations (config-driven)
 * - Annotations: Add comments and tags (config-driven)
 *
 * **Performance Optimized**: React.memo prevents re-renders when props unchanged
 *
 * @example
 * ```tsx
 * const { modalHandlers, handlers } = useSeedsActions({...});
 *
 * <SeedsActions
 *   seedType="domain"
 *   selectedSeeds={selectedSeeds}
 *   selectedRowsCount={selectedKeys.length}
 *   modalHandlers={modalHandlers}
 *   actionHandlers={handlers}
 *   onClearSelection={clearSelection}
 * />
 * ```
 */
const SeedsActionsComponent: React.FC<SeedsActionsProps> = ({
  seedType,
  selectedSeeds,
  selectedRowsCount,
  modalHandlers,
  actionHandlers,
  onClearSelection: _onClearSelection,
  className,
  hideLabel,
  hideActions,
  hideAddSeed,
}) => {
  const { profiles } = useScanProfiles();
  const axios = useAxios();

  const profileOptions =
    profiles.length > 0
      ? [
          { label: 'Default (Global)', value: '' },
          ...profiles.map(p => ({
            label: p.name,
            value: p.id,
          })),
        ]
      : undefined;

  const handleProfileChange = async (profileId: string) => {
    try {
      for (const seed of selectedSeeds) {
        await axios.put('/seed', { key: seed.key, profile: profileId });
      }
      // Optimistically update cached seed data
      queryClient.setQueriesData(
        {
          predicate: query => {
            const qk = query.queryKey as string[];
            return qk[0] === 'MY' && qk[1] === 'seed';
          },
        },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          const data = old as { pages?: { data: Seed[] }[] };
          if (!data.pages) return old;
          return {
            ...data,
            pages: data.pages.map(page => ({
              ...page,
              data: page.data.map((s: Seed) =>
                selectedSeeds.some(sel => sel.key === s.key)
                  ? { ...s, profile: profileId || undefined }
                  : s
              ),
            })),
          };
        }
      );
      toast.success(
        profileId ? 'Scan profile updated' : 'Scan profile removed'
      );
    } catch {
      toast.error('Failed to update scan profile');
    }
  };

  // Get configuration for this seed type
  const config = getSeedTypeConfig(seedType);

  // Determine which scan types to enable based on selected seeds
  const hasWebApps = selectedSeeds.some(seed =>
    isSeedWebApplication(seed as SeedDiscriminated)
  );
  const hasDomainOrIP = selectedSeeds.some(
    seed =>
      isSeedDomain(seed as SeedDiscriminated) ||
      isSeedIP(seed as SeedDiscriminated)
  );
  const hasSelection = selectedSeeds.length > 0;
  const { summonAurelius } = useSummonAurelius();

  // Build action configurations (config-driven)
  const actions = [];

  // Ask Marcus action - always first position
  actions.push(
    buildGenericCustomAction({
      render: () => (
        <AskMarcusButton
          onClick={() =>
            summonAurelius({
              type: 'seed',
              keys: selectedSeeds.map(s => s.key),
              displayNames: selectedSeeds.map(s => s.name || s.identifier),
            })
          }
          disabled={selectedRowsCount === 0}
          data-testid="seeds-ask-marcus-button"
        />
      ),
      position: 'first',
      testId: 'seeds-ask-marcus-wrapper',
    })
  );

  // Annotate actions (Comment, Tag, Remove Tag) - only if config allows
  if (config.actions.comment && config.actions.tag) {
    actions.push(
      buildAnnotateAction({
        onCommentClick: modalHandlers.openCommentModal,
        onTagClick: modalHandlers.openTagModal,
        onRemoveTagClick: modalHandlers.openRemoveTagModal,
        labels: { comment: 'Add Note' },
        testId: 'seeds-annotate-dropdown',
      })
    );
  }

  // Manage actions (Approve, Reject, Freeze, Delete) - config-driven
  const statusOptions = [];
  if (config.actions.approve) {
    statusOptions.push({
      label: 'Approve',
      value: SeedStatus.Accept,
      icon: <CheckCircle className="size-4" />,
      shortcut: '⌥ A',
    });
  }
  if (config.actions.reject) {
    statusOptions.push({
      label: 'Reject',
      value: SeedStatus.Reject,
      icon: <XCircle className="size-4" />,
      shortcut: '⌥ R',
    });
  }

  // Only add manage action if we have status options OR freeze/delete
  if (
    statusOptions.length > 0 ||
    config.actions.freeze ||
    config.actions.delete
  ) {
    // No-op handler for disabled actions
    const noOp = () => {};

    actions.push(
      buildManageAction({
        onFreezeClick: config.actions.freeze
          ? () => void actionHandlers.handleFreeze()
          : noOp,
        onActivateClick: config.actions.approve
          ? () => void actionHandlers.handleActivate()
          : noOp,
        onDeleteClick: config.actions.delete
          ? () => void actionHandlers.handleDelete()
          : noOp,
        statusOptions,
        onStatusChangeClick: async status => {
          switch (status as SeedStatus) {
            case SeedStatus.Accept:
              await actionHandlers.handleApprove();
              break;
            case SeedStatus.Reject:
              await actionHandlers.handleReject();
              break;
          }
        },
        profileOptions,
        onProfileChangeClick: handleProfileChange,
        currentProfileId:
          selectedSeeds.length === 1
            ? selectedSeeds[0].profile || ''
            : undefined,
        labels: { activate: 'Unfreeze' },
        shortcuts: config.actions.freeze
          ? { freeze: ' ⌥ F', delete: ' ⌥ D' }
          : { delete: ' ⌥ D' },
        testId: 'seeds-managed-dropdown',
      })
    );
  }

  // Scan actions - only if config allows
  if (config.actions.scan) {
    actions.push(
      buildScanActionHandlers({
        handlers: {
          onComprehensiveScan: () => actionHandlers.handleComprehensiveScan(),
          onAffiliationScan: () => actionHandlers.handleAffiliationScan(),
          onNetworkScan: () => actionHandlers.handleNetworkScan(),
          onPortScan: () => actionHandlers.handlePortScan(),
          onCrawlScan: () => actionHandlers.handleWebCrawl(),
          onWebScan: () => actionHandlers.handleWebScan(),
        },
        disabledActions: {
          network: !hasDomainOrIP,
          port: !hasDomainOrIP,
          crawl: !hasWebApps,
          webscan: !hasWebApps,
        },
        labels: {
          crawl: 'Web Crawl',
          webscan: 'Web Scan (DAST)',
        },
        disabled: !hasSelection,
        testId: 'seeds-scan-dropdown',
      })
    );
  }

  // Share action - only if config allows
  if (config.actions.share) {
    actions.push(
      buildShareAction({
        variant: 'button-only',
        onShareClick: actionHandlers.handleShare,
        testId: 'seeds-share-button',
      })
    );
  }

  // Add Seed action - shown in table context, hidden in drawer
  // Uses rounded styleType for pill shape, with className override for purple fill
  if (seedType !== 'preseed' && !hideAddSeed) {
    actions.push(
      buildGenericCustomAction({
        render: () => (
          <Button
            onClick={modalHandlers.openAddSeedModal}
            variant="outline"
            size="sm"
            className="bg-chariot-brand-primary border-chariot-brand-primary hover:bg-chariot-brand-secondary rounded-full text-white hover:text-white"
            data-testid="seeds-add-button"
            aria-label="Add Seed"
          >
            <PlusCircleIcon className="size-4" />
            Add Seed
          </Button>
        ),
        testId: 'seeds-add-button',
        position: 'first',
      })
    );
  }

  // Import action - always enabled (no config check)
  actions.push(
    buildImportAction({
      onImportClick: modalHandlers.openImportModal,
      disabled: false, // Always enabled
      testId: 'seeds-import-button',
    })
  );

  // Export action - always enabled (no config check)
  actions.push(
    buildExportAction({
      onExportClick: modalHandlers.openExportModal,
      disabled: selectedRowsCount === 0,
      alwaysEnabled: false,
      testId: 'seeds-export-button',
    })
  );

  return (
    <TableActionsBar
      selectedItems={selectedSeeds}
      actions={actions}
      disabled={selectedRowsCount === 0}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="seeds"
    />
  );
};

SeedsActionsComponent.displayName = 'SeedsActions';

export const SeedsActions = React.memo(SeedsActionsComponent);
